<template>
  <Pane :value="filtersPaneIsOpen"
        :tabs="tabs"
        location="left"
        current-tab="filters"
        temporary
        data-locator="documents-filters-pane"
        @input="onPaneValueChanged"
        @update:modelValue="onPaneValueChanged"
  >
    <template #content-filters>
        <v-row>
          <v-col>
            <div class="font-weight-bold mb-2">{{ $t('project.documents.DocumentsFiltersPane.dates') }}</div>
            <AppDateRangePicker v-model="localFilters.dates" multiple label="" />
          </v-col>
        </v-row>
        <template v-if="members.length > 0 && !loadingMembers">
          <v-divider class="my-4"/>
          <v-row>
            <v-col>
              <div class="font-weight-bold mb-2">{{ $t('project.documents.DocumentsFiltersPane.members') }}</div>
              <div>
                <v-checkbox v-for="(member, index) in members.length > 10 && !displayMoreMembers ? shortMembers : members"
                            :key="`member-${index}`"
                            v-model="localFilters.members"
                            :value="member"
                            :label="member.fullName"
                            hide-details
                            class="my-1"
                />
              </div>
            </v-col>
          </v-row>
          <v-row v-if="members.length > 10 && !displayMoreMembers">
            <v-col>
              <app-button
                type="outlined"
                variant="neutral"
                size="small"
                data-locator="display-more-members-button"
                @click="displayMoreMembers = true"
              >
                {{ $t('project.documents.DocumentsFiltersPane.displayMoreMembers') }}
                <app-material-icon name="chevron-down" type="fa" size="xxs" />
              </app-button>
            </v-col>
          </v-row>
          <v-row v-if="members.length > 10 && displayMoreMembers">
            <v-col>
              <app-button
                type="outlined"
                variant="neutral"
                size="small"
                data-locator="display-less-members-button"
                @click="displayMoreMembers = false"
              >
                {{ $t('project.documents.DocumentsFiltersPane.displayLessMembers') }}
                <app-material-icon name="chevron-up" type="fa" size="xxs" />
              </app-button>
            </v-col>
          </v-row>
        </template>
        <v-row v-if="loadingMembers">
          <v-col>
            <div class="d-flex justify-center">
              <AppLoader />
            </div>
          </v-col>
        </v-row>
      <v-row>
        <v-col>
          <AppButtonVuetify
                      block
                      data-locator="clear-button"
                      @click="onClearAllFilters"
          >
            {{$t('common.clear')}}
          </AppButtonVuetify>
        </v-col>
        <v-col>
          <AppButtonVuetify color="primary"
                      block
                      data-locator="apply-button"
                      @click="onApplyFilters"
          >
            {{$t('common.apply')}}
          </AppButtonVuetify>
        </v-col>
      </v-row>
    </template>
  </Pane>
</template>

<script>
import { mapActions, mapMutations, mapState } from 'vuex'

import AppDateRangePicker from '@/common/AppDateRangePicker.vue'
import AppLoader from '@/common/AppLoader.vue'
import AppButtonVuetify from '@/common/buttons/AppButton.vue'
import Pane from '@/common/Pane.vue'
import { ISOToShortenedDate } from '@/common/utils/dates'
import { Filter } from '@/models/filter.model'
import documentsService from '@/services/documents.service'
import { CHANGE_PAGINATED_DOCUMENTS_FILTERS } from '@/store/modules/documents/action_types'
import { SET_FILTERS_PANE_IS_OPEN } from '@/store/modules/documents/mutation_types'


const EMPTY_FILTERS = {
  dates: [],
  members: [],
}

export default {
  name: 'DocumentsFiltersPane',
  components: { Pane, AppButtonVuetify, AppDateRangePicker, AppLoader },
  data () {
    return {
      displayMoreMembers: false,
      localFilters: { ...EMPTY_FILTERS },
      tabs: [{ id: 'filters', title: this.$t('common.filters') }],
      loadedMembersFolderId: null,
      loadingMembers: false,
      members: [],
    }
  },
  computed: {
    ...mapState('documents', ['paginatedDocuments', 'paginatedDocumentsFilters', 'filtersPaneIsOpen', 'paginatedDocumentsCurrentFolderId']),
    shortMembers () {
      return this.members.slice(0, 10)
    },
  },
  watch: {
    filtersPaneIsOpen (value) {
      if (value) {
        this.syncAppliedFiltersWithLocals()
        this.loadMembers()
      }
    },
  },
  methods: {
    ...mapMutations('documents', [SET_FILTERS_PANE_IS_OPEN]),
    ...mapActions('documents', [CHANGE_PAGINATED_DOCUMENTS_FILTERS]),
    async loadMembers() {
      if (this.loadedMembersFolderId === this.paginatedDocumentsCurrentFolderId) {
       return
      }

      this.loadingMembers = true

      try {
        const response = await documentsService.getFolderUpdateUsers(
          this.$route.params.mnemo,
          this.paginatedDocumentsCurrentFolderId === 'recentFolder' ? 'recent' : this.paginatedDocumentsCurrentFolderId,
        )
        this.members = response.data
        this.loadedMembersFolderId = this.paginatedDocumentsCurrentFolderId
      } finally {
        this.loadingMembers = false
      }
    },
    applyFilters () {
      const datesFilters = this.localFilters.dates.length > 0
        ? [
          new Filter(
            'dates',
            this.$t('project.documents.DocumentsFiltersPane.dates'),
            this.localFilters.dates,
            `${ISOToShortenedDate(this.localFilters.dates[0])} - ${ISOToShortenedDate(this.localFilters.dates[1])}`,
          )]
        : []

      const membersFilters = this.localFilters.members.map(member =>
        new Filter('members', this.$t('common.user'), member.id, member.fullName),
      )

      const filters = [
        ...datesFilters,
        ...membersFilters,
      ]

      this.CHANGE_PAGINATED_DOCUMENTS_FILTERS(filters)
    },
    closePane () {
      this.SET_FILTERS_PANE_IS_OPEN(false)
    },
    onApplyFilters () {
      this.applyFilters()
      this.closePane()
    },
    onClearAllFilters () {
      this.localFilters = { ...EMPTY_FILTERS }
      this.applyFilters()
    },
    onPaneValueChanged (isOpen) {
      if (!isOpen) {
        this.closePane()
      }
    },
    syncAppliedFiltersWithLocals () {
      const datesFilters = this.paginatedDocumentsFilters.find(filter => filter.category.key === 'dates')
      this.localFilters.dates = datesFilters?.value.key ?? []

      this.localFilters.members = this.paginatedDocumentsFilters
        .filter(filter => filter.category.key === 'members')
        .map(filter => ({ id: filter.value.key, fullName: filter.value.label }))
    },
  },
}
</script>

<style scoped lang="scss">
:deep(.v-icon.notranslate.fas.fa-caret-down.theme--light) {
  display: none;
}
:deep(.v-text-field__details) {
  display: none;
}
</style>
